import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import usePaymentFlow from './PaymentPage/usePaymentFlow';
import BookingSummaryCard from './PaymentPage/BookingSummaryCard';
import AmountCard from './PaymentPage/AmountCard';
import InitiatePaymentSection from './PaymentPage/InitiatePaymentSection';
import CardForm from './PaymentPage/CardForm';

/**
 * Customer payment page — Pocket Pay flow.
 *
 * Two visual steps:
 *   1. Initiate Payment   → POST /api/bookings/{id}/payment → redirect to Pocket Pay
 *   2. Card Form (fallback) → POST /api/bookings/{id}/payment/process
 *
 * All state + side-effects live in `usePaymentFlow`; this component is a
 * thin orchestrator that composes the presentation pieces.
 */
export default function PaymentPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const {
    booking,
    processing,
    paymentInitiated,
    paymentData,
    cardDetails,
    errors,
    updateCardField,
    fillTestCard,
    initiatePayment,
    processPayment,
  } = usePaymentFlow(bookingId);

  if (!booking) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const finalTotal = booking.final_total ?? 0;
  const orderIdShort = paymentData?.pocket_pay_order_id?.substring(0, 8);

  return (
    <div className="mobile-container min-h-screen pb-6" data-testid="payment-page">
      <div className="bg-primary text-primary-foreground p-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/booking/${bookingId}`)}
            data-testid="back-button"
            className="text-primary-foreground hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-black font-heading uppercase">Payment</h1>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <BookingSummaryCard booking={booking} />
        <AmountCard booking={booking} />

        {paymentInitiated ? (
          <CardForm
            card={cardDetails}
            errors={errors}
            processing={processing}
            orderIdShort={orderIdShort}
            amount={finalTotal}
            onChange={updateCardField}
            onFillTestCard={fillTestCard}
            onSubmit={processPayment}
          />
        ) : (
          <InitiatePaymentSection
            amount={finalTotal}
            processing={processing}
            onInitiate={initiatePayment}
          />
        )}
      </div>
    </div>
  );
}
